import React, { memo, useCallback, useRef } from "react";
import {
  DrawerBody,
  DrawerFooter,
  HStack,
  Stack,
  VStack,
} from "@chakra-ui/react";
import ForwardToRelatedMessageThread from "./forward-to-related-message-thread";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";
import QuillMessageComposer, {
  IQuillEditorHandle,
} from "../../messaging/compose/quill-message-composer";
import UserTagGroupSelector, {
  Option,
} from "modules/selectors/user-tag-group-selector";
import ComposeMessageThreadSubject from "../../messaging/_shared/compose-message-thread-subject";
import { MessageThreadType } from "gql/graphql";
import ComposeTaskAssigneeSelector from "modules/messaging/tasks/compose-task-assignee-selector";
import colors from "app/colors";
import { PersonCircleIcon, ClockIcon } from "modules/icons";
import TaskDueDateSelector from "modules/messaging/tasks/task-due-date-selector";

interface IProps {
  draftId: string;
}

const ForwardToCompose = ({ draftId }: IProps) => {
  const { draft, updateCalloutUserIds, userValues } = useMessageThreadDraft({
    draftId,
  });

  const onSelectedCalloutsChanged = useCallback(
    (options: Option[]) => {
      updateCalloutUserIds(options.map((option) => option.id));
    },
    [updateCalloutUserIds],
  );

  const quillEditorRef = useRef<IQuillEditorHandle>(null);

  if (!draft) return null;

  if (draft.type === MessageThreadType.Thread) {
    return (
      <>
        <DrawerBody>
          <Stack pt={3}>
            <VStack w="full" alignItems="flex-start" spacing={1}>
              <ComposeMessageThreadSubject draftId={draftId} />
            </VStack>
            <ForwardToRelatedMessageThread
              draftId={draftId}
              quillEditorRef={quillEditorRef}
            />
          </Stack>
        </DrawerBody>

        <DrawerFooter p={0} alignItems="flex-end">
          <QuillMessageComposer
            ref={quillEditorRef}
            draftId={draftId}
            type={draft.type}
          />
        </DrawerFooter>
      </>
    );
  }

  if (draft.type === MessageThreadType.Chat) {
    return (
      <>
        <DrawerBody>
          <Stack pt={3}>
            <UserTagGroupSelector
              isMultiSelect
              types={["user"]}
              onSelectedOptionsChanged={onSelectedCalloutsChanged}
              placeHolder="Send to"
              value={userValues}
            />
            <ForwardToRelatedMessageThread
              draftId={draftId}
              quillEditorRef={quillEditorRef}
            />
          </Stack>
        </DrawerBody>

        <DrawerFooter p={0} alignItems="flex-end">
          <QuillMessageComposer
            ref={quillEditorRef}
            draftId={draftId}
            type={draft.type}
          />
        </DrawerFooter>
      </>
    );
  }

  if (draft.type === MessageThreadType.Task) {
    return (
      <>
        <DrawerBody>
          <Stack spacing={4}>
            <ComposeMessageThreadSubject draftId={draftId} />
            <HStack w="full">
              <PersonCircleIcon fill={colors.gray30} boxSize={5} />
              <ComposeTaskAssigneeSelector draftId={draftId} />
            </HStack>
            <HStack w="full">
              <ClockIcon fill={colors.gray30} boxSize={5} />
              <TaskDueDateSelector draftId={draftId} />
            </HStack>
            <ForwardToRelatedMessageThread
              draftId={draftId}
              quillEditorRef={quillEditorRef}
            />
          </Stack>
        </DrawerBody>

        <DrawerFooter p={0} alignItems="flex-end">
          <QuillMessageComposer
            ref={quillEditorRef}
            draftId={draftId}
            type={draft.type}
          />
        </DrawerFooter>
      </>
    );
  }

  return null;
};

export default memo(ForwardToCompose);
